import React, {useState, useEffect} from 'react'
import {KTSVG} from '../../../_metronic/helpers'
import {Modal} from 'react-bootstrap'
import {Dispatch} from 'redux'
import {IsCurrentPlanFree, isCurrentPlanLimitHigher} from '../../services/Functional'
import {useDispatch, useSelector, shallowEqual} from 'react-redux'
import {RootState} from '../../../setup'
import {getDashboardAnalytics} from '../../../setup/redux/common/actions'
import CustomTooltip from '../../../_metronic/tooltip/CustomTooltip'
import BannerCard from './BannerCard'

interface SubscriptionPlan {
  id: string | number
  name: string
  type: string
  monthly_price?: number
  yearly_price?: number
  description?: string
  allowed_number_of_users?: number
  number_of_active_inspection_assets?: number
  number_of_active_inspection_sites?: number
  number_of_active_inspection_projects?: number
  number_of_credits?: number
}

interface SubscriptionPlanDisplayProps {
  selectedType: 'yearly' | 'monthly'
  onTypeChange: (type: 'yearly' | 'monthly') => void
  onPlanSelect?: (plan: SubscriptionPlan) => void
  showActionButtons?: boolean
  onUpgrade?: (plan: SubscriptionPlan) => void
  onRenew?: (plan: SubscriptionPlan) => void
  className?: string
  selectedPlan?: SubscriptionPlan | null
  subscriptionPlans: SubscriptionPlan[]
  disableHover?: boolean
  currentPlan?: any
  isUpgrading?: boolean
  currentPlanBadgeText?: string
  disableCursorPointer?: boolean
  isPreFiltered?: boolean
  onFreePlanGetStarted?: () => void
  isCompanyOnGracePeriod?: boolean
}

const SubscriptionPlanDisplay: React.FC<SubscriptionPlanDisplayProps> = ({
  selectedType,
  onTypeChange,
  onPlanSelect,
  showActionButtons = false,
  onUpgrade,
  onRenew,
  className = '',
  selectedPlan = null,
  subscriptionPlans,
  disableHover = false,
  currentPlan = null,
  isUpgrading = false,
  currentPlanBadgeText = 'Current Plan',
  disableCursorPointer = false,
  isPreFiltered = false,
  onFreePlanGetStarted,
  isCompanyOnGracePeriod = false,
}) => {
  const dispatch: Dispatch<any> = useDispatch()
  const isCurrentPlanFree = IsCurrentPlanFree()
  const isExpiredPlan = currentPlanBadgeText === 'Expired Plan'
  const [filteredList, setFilteredList] = useState<SubscriptionPlan[]>([])
  const [isReadMoreAvail, setIsReadMoreAvail] = useState(false)
  const [isPlanIdDescription, setIsPlanIdDescription] = useState<string>('')
  const [isPlanType, setIsPlanType] = useState<string>('')

  const {dashboardAnalytics}: any = useSelector<RootState>(
    (state) => ({
      dashboardAnalytics: state.common.dashboardAnalytics,
    }),
    shallowEqual
  )

  useEffect(() => {
    if (isPreFiltered) {
      dispatch(getDashboardAnalytics())
    }
  }, [])

  const sortPlansByType = (plans: SubscriptionPlan[]): SubscriptionPlan[] => {
    const paidPlans = plans.filter((plan: any) => plan.type === 'paid')
    const enterprisePlans = plans.filter((plan: any) => plan.type === 'enterprise')
    const sortedPaidPlans = paidPlans.sort((a, b) => {
      const aPrice = selectedType === 'yearly' ? a.yearly_price || 0 : a.monthly_price || 0
      const bPrice = selectedType === 'yearly' ? b.yearly_price || 0 : b.monthly_price || 0
      return aPrice - bPrice
    })
    const combinedPlans = sortedPaidPlans.concat(enterprisePlans)
    return combinedPlans
  }

  useEffect(() => {
    let plansToFilter = subscriptionPlans
    plansToFilter = subscriptionPlans.filter((plan) => plan.type !== 'free')
    const sortedPlans = sortPlansByType(plansToFilter)
    setFilteredList(sortedPlans)
  }, [subscriptionPlans, selectedType, isPreFiltered])

  const getPlanTypeLabel = (plan: SubscriptionPlan) => {
    return plan.name
  }

  const getPlanDescription = (plan: SubscriptionPlan) => {
    return plan.description || ''
  }

  const getPlanFeatures = (plan: SubscriptionPlan) => {
    if (plan.type === 'enterprise') {
      return [
        'Unlimited users',
        'Unlimited assets',
        'Unlimited sites',
        'Unlimited projects',
        'Unlimited decent credits',
      ]
    }

    const features = []

    if (plan.allowed_number_of_users) {
      features.push(`${plan.allowed_number_of_users} users`)
    }

    if (plan.number_of_active_inspection_assets) {
      features.push(`${plan.number_of_active_inspection_assets} assets`)
    }

    if (plan.number_of_active_inspection_sites) {
      features.push(`${plan.number_of_active_inspection_sites} sites`)
    }

    if (plan.number_of_active_inspection_projects) {
      features.push(`${plan.number_of_active_inspection_projects} projects`)
    }

    if (plan.number_of_credits) {
      features.push(`${plan.number_of_credits} decent credits`)
    }

    if (features.length === 0) {
      return ['0 users', '0 assets', '0 sites', '0 projects', '0 decent credits']
    }

    return features
  }

  const getPlanPrice = (plan: SubscriptionPlan) => {
    if (plan.type === 'enterprise') return 'Custom'
    const price = selectedType === 'yearly' ? plan.yearly_price : plan.monthly_price
    return price ? `€ ${price}` : '€ 0'
  }

  const isSelectedPlan = (plan: SubscriptionPlan) => {
    return selectedPlan && selectedPlan.id === plan.id
  }

  const isCurrentPlan = (plan: SubscriptionPlan) => {
    if (!currentPlan || !currentPlan.subscription) return false
    const isIdMatch = currentPlan.subscription.id === plan.id
    if (currentPlan.subscription.type === 'free') {
      return isIdMatch
    }
    const isTypeMatch = currentPlan.subscription_type === selectedType
    return isIdMatch && isTypeMatch
  }

  const shouldShowUpgradeButton = (plan: SubscriptionPlan) => {
    return showActionButtons && !isCurrentPlan(plan) && !(isCurrentPlan(plan) && isCurrentPlanFree)
  }

  const shouldShowRenewButton = (plan: SubscriptionPlan) => {
    return showActionButtons && isCurrentPlan(plan) && !isCurrentPlanFree && isExpiredPlan
  }

  const handlePlanClick = (plan: SubscriptionPlan) => {
    if (onPlanSelect) {
      onPlanSelect(plan)
    }
  }

  const handleUpgrade = (plan: SubscriptionPlan) => {
    if (onUpgrade) {
      onUpgrade(plan)
    }
  }

  const handleRenew = (plan: SubscriptionPlan) => {
    if (onRenew) {
      onRenew(plan)
    }
  }

  const handleReadMoreOpen = (planId: string, planType: string) => {
    setIsReadMoreAvail(true)
    setIsPlanIdDescription(planId)
    setIsPlanType(planType)
  }

  const handleClose = () => {
    setIsReadMoreAvail(false)
  }

  return (
    <div className={className}>
      {currentPlan && currentPlan.subscription && currentPlan.subscription.type === 'free' && (
        <BannerCard
          title={isExpiredPlan ? 'Your Free Plan has Expired' : 'You are currently on a Free Plan'}
          description={
            isExpiredPlan
              ? 'Upgrade to a paid plan to continue using the service and unlock more features.'
              : 'Upgrade to unlock more features and increase your limits.'
          }
          variant={isExpiredPlan ? 'expired' : 'active'}
          className='mb-6'
        />
      )}

      <div className='d-flex align-items-center justify-content-center'>
        <button
          type='button'
          onClick={() => onTypeChange('monthly')}
          className={`btn rounded-0 border-bottom border-${selectedType === 'monthly' ? 'primary' : 'secondary'} text-capitalize py-2 px-6`}
        >
          <h3 className={`fw-normal ${selectedType === 'monthly' ? 'text-primary' : ''}`}>
            Monthly
          </h3>
        </button>
        <button
          type='button'
          onClick={() => onTypeChange('yearly')}
          className={`btn rounded-0 border-bottom border-${selectedType === 'yearly' ? 'primary' : 'secondary'} text-capitalize py-2 px-6`}
        >
          <h3 className={`fw-normal ${selectedType === 'yearly' ? 'text-primary' : ''}`}>Yearly</h3>
        </button>
      </div>

      {/* Plan Cards */}
      <div className='row g-8 justify-content-center mt-2'>
        {filteredList.length !== 0 &&
          filteredList.map((plan: SubscriptionPlan, index: number) => {
            const planTypeLabel = getPlanTypeLabel(plan)
            const planDescription = getPlanDescription(plan)
            const planFeatures = getPlanFeatures(plan)
            const planPrice = getPlanPrice(plan)

            const currentPlanLimitHigher =
              dashboardAnalytics && isPreFiltered
                ? isCurrentPlanLimitHigher(dashboardAnalytics, plan)
                : false

            return (
              <div key={plan.id} className='col-lg-4 position-relative'>
                {isCurrentPlan(plan) && (
                  <span
                    className={`badge fw-medium fs-8 current-plan-badge ${
                      isExpiredPlan || isCompanyOnGracePeriod
                        ? 'inactive-status'
                        : 'badge-light-success'
                    }`}
                  >
                    {isCompanyOnGracePeriod ? 'Expired Plan' : currentPlanBadgeText}
                  </span>
                )}
                <div
                  className={`subscription-plan-card border rounded p-6 d-flex flex-column h-100 ${
                    isSelectedPlan(plan) ? 'border-primary' : 'border-secondary'
                  }`}
                  style={{
                    cursor: disableCursorPointer ? 'default' : onPlanSelect ? 'pointer' : 'default',
                    transform: isSelectedPlan(plan) ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: isSelectedPlan(plan) ? '#f8f9ff' : 'white',
                    boxShadow: isSelectedPlan(plan) ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelectedPlan(plan) && !disableHover) {
                      e.currentTarget.style.borderColor = '#0d6efd'
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)'
                      e.currentTarget.style.transform = 'scale(1.01)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelectedPlan(plan) && !disableHover) {
                      e.currentTarget.style.borderColor = '#6c757d'
                      e.currentTarget.style.boxShadow = 'none'
                      e.currentTarget.style.transform = 'scale(1)'
                    }
                  }}
                  onClick={() => handlePlanClick(plan)}
                >
                  <div className='border-bottom border-secondary pb-6'>
                    <h3 className='fw-medium'>{planTypeLabel}</h3>
                    <div className='d-flex align-items-center gap-3'>
                      <h1 className='text-capitalize display-6'>{planPrice}</h1>
                    </div>
                  </div>
                  <div
                    className={`py-6 flex-grow-1 ${
                      shouldShowUpgradeButton(plan) || shouldShowRenewButton(plan)
                        ? 'border-bottom border-secondary'
                        : ''
                    }`}
                  >
                    <span className='fs-6'>
                      {planDescription && planDescription.length > 65 ? (
                        <>
                          {planDescription.slice(0, 65)}
                          {'... '}
                          <span
                            className='text-primary link-primary cursor-pointer'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleReadMoreOpen(planDescription, plan.name)
                            }}
                          >
                            Read More
                          </span>
                        </>
                      ) : (
                        planDescription
                      )}
                    </span>
                    <div className='d-flex flex-column gap-2 mt-3'>
                      {planFeatures.map((feature: string, featureIndex: number) => {
                        return (
                          <div key={featureIndex} className='d-flex align-items-center gap-2'>
                            <KTSVG
                              path='/media/icons/duotune/general/check.svg'
                              className='svg-icon-2'
                            />
                            <span className='feature-text'>{feature}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  {shouldShowUpgradeButton(plan) && (
                    <div className='mt-auto position-relative'>
                      {currentPlanLimitHigher ? (
                        <CustomTooltip
                          message="You're currently using more projects, sites, or assets than this plan allows. Please adjust your usage before switching."
                          position='top'
                          MenuallyPosition
                          customStyles={{
                            position: 'absolute',
                            top: '-100%',
                            left: '0',
                            maxWidth: '100%',
                            transform: 'translateY(-50%)',
                          }}
                        >
                          <button
                            className='btn btn-primary border border-primary w-100'
                            disabled={currentPlanLimitHigher || isUpgrading}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleUpgrade(plan)
                            }}
                          >
                            Upgrade
                          </button>
                        </CustomTooltip>
                      ) : (
                        <button
                          className='btn btn-primary border border-primary w-100'
                          disabled={currentPlanLimitHigher || isUpgrading}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleUpgrade(plan)
                          }}
                        >
                          Upgrade
                        </button>
                      )}
                    </div>
                  )}
                  {shouldShowRenewButton(plan) && (
                    <div className='mt-auto'>
                      <button
                        className='btn btn-primary border border-success w-100'
                        disabled={isUpgrading}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRenew(plan)
                        }}
                      >
                        Renew Now
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
      </div>
      {!isPreFiltered && subscriptionPlans.some((plan) => plan.type === 'free') && (
        <div className='card bg-light-primary border-primary mt-8'>
          <div className='card-body d-flex align-items-center justify-content-between p-6'>
            <div className='d-flex flex-column'>
              <h4 className='fw-bold text-primary mb-2'>Start with our Free Plan (14-day trial)</h4>
              <p className='text-gray-600 mb-0'>
                Get started with our free plan and explore all the features. No credit card
                required.
              </p>
            </div>
            <button type='submit' className='btn btn-primary btn-lg' onClick={onFreePlanGetStarted}>
              Get Started
            </button>
          </div>
        </div>
      )}
      <Modal
        show={isReadMoreAvail}
        onHide={handleClose}
        tabIndex={0}
        className='modal-lg modal-dialog-scrollable'
      >
        <Modal.Header closeButton>
          <Modal.Title className='fs-1 text-capitalize'>
            {isPlanType ? isPlanType : 'selected '} Description
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='enterPriseModal enterprise-container'>
          {isPlanIdDescription ? isPlanIdDescription : ''}
        </Modal.Body>
        <Modal.Footer>
          <button
            type='button'
            className='btn btn-light'
            data-bs-dismiss='modal'
            onClick={handleClose}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default SubscriptionPlanDisplay
